import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AG_GRID_LOCALE_EN,
  AG_GRID_LOCALE_DE,
} from '@ag-grid-community/locale';
import { t } from '@lingui/macro';
import { GridReadyEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { ErrorCard } from 'components/ErrorCard/ErrorCard';
import {
  TTableStatus,
  useApplicationTableState,
} from 'contexts/ApplicationTableStateContext/ApplicationTableStateContext';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useAmplitude } from 'hooks/useAmplitude';
import { useLocale } from 'hooks/useLocale';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';
import { APPLICATION_STATUS } from 'utils/applications/applications.config';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useApplicationTable } from './ApplicationTable.context';
import styles from './ApplicationTable.module.css';
import { ColumnDefinitions } from './ApplicationTableColumns.helpers';
import {
  bindListFilters,
  registerNativeAgGridFilters,
} from './applicationUtils';
import { ApplicationStatusCell } from './cells/ApplicationStatusCell/ApplicationStatusCell';
import { ApprovalAmountCell } from './cells/ApprovalAmountCell/ApprovalAmountCell';
import { CompanyCell } from './cells/CompanyCell/CompanyCell';
import { CreatedCell } from './cells/CreatedCell/CreatedCell';
import { OfferExpiryDateCell } from './cells/OfferExpiryDateCell/OfferExpiryDateCell';
import { ProductTypeCell } from './cells/ProductTypeCell/ProductTypeCell';
import { TermLengthCell } from './cells/TermLengthCell/TermLengthCell';
import { NoRowsWithSearchOverlay } from './NoRowsWithSearchOverlay';
import { TableColumnHeader } from './tableColumnHeader/TableColumnHeader';
import { TableSelectValuesFilter } from './TableSelectValuesFilter/TableSelectValuesFilter';
import { TableStatus } from './TableStatus/TableStatus';
import { useContinueRedirect } from './useContinueRedirect.hook';

export const ApplicationTable = () => {
  const [isGridReady, setIsGridReady] = useState(false);
  const {
    state: { error, applications },
    getApplicationLoading,
  } = useApplicationTable();
  const {
    mounted: tableStateInitialized,
    setGridApi,
    filters,
    getFilterValues,
    saveFilter,
    removeFilter,
    setStatus,
    status,
    setNumberOfRows,
    initialApplicationsLoaded,
    setInitialApplicationsLoaded,
    externalStatusFilter,
  } = useApplicationTableState();
  const { introducer, introducerCompany, isContextError } =
    useIntroducerContext();
  const { locale } = useLocale();
  const gridRef = useRef<AgGridReact>(null);
  const filterRef = useRef('all');
  const { trackEvent } = useAmplitude();
  const { handleRedirect } = useContinueRedirect();
  const { splitTests } = useIntroducerContext();
  const navigate = useNavigate();

  useEffect(() => {
    setGridApi(gridRef.current?.api);
    if (getApplicationLoading()) {
      setStatus('loading');
    } else if (applications) {
      let newStatus: TTableStatus = 'success';
      if (applications.length === 0) {
        newStatus = initialApplicationsLoaded ? 'no results' : 'empty';
      }
      setStatus(newStatus);
      setNumberOfRows(applications.length);
      if (!initialApplicationsLoaded && applications.length > 0) {
        setInitialApplicationsLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGridReady, applications]);

  // Bind the initial filters to the table context
  useEffect(() => {
    if (tableStateInitialized && isGridReady && gridRef.current) {
      bindListFilters(gridRef.current.api, { getFilterValues });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableStateInitialized, isGridReady, getApplicationLoading, filters]);

  useEffect(() => {
    // https://stackoverflow.com/a/58562733
    // ag-grid React never updates the callback for isExternalFilterPresent
    // The way around this is to use a ref for any of the values that isExternalFilterPresent
    // closes over so you always access the most up to date value.
    filterRef.current = externalStatusFilter || 'all';
    gridRef.current?.api?.onFilterChanged();
  }, [externalStatusFilter]);

  const sizeColumnsToFit = useCallback((params: GridReadyEvent) => {
    params.api.sizeColumnsToFit();
  }, []);

  const onRowClicked = useCallback(
    (params: GridReadyEvent) => {
      const rowData = params.api.getSelectedRows()[0];
      const stateKey = rowData.customer?.state_key;
      const applicationStatus = rowData.status;

      const isPartnerChannel = rowData.is_partner_channel;

      trackEvent(AMPLITUDE_EVENTS.dashboardRowClicked, {
        Status: _.capitalize(_.startCase(rowData.status)),
        Channel: isPartnerChannel ? 'Partner' : 'Broker',
      });

      if (isPartnerChannel) {
        return navigate(
          PATH_NAMES.application({ applicationId: rowData.application_uid }),
        );
      }

      if (applicationStatus === 'open') {
        const isSignupFinished = rowData.customer?.date_sign_up_finished;

        if (isSignupFinished) {
          return navigate(
            PATH_NAMES.applicationDocuments({
              applicationId: rowData.application_uid,
            }),
          );
        }

        return handleRedirect(stateKey, rowData.application_uid);
      }

      if (applicationStatus === APPLICATION_STATUS.action_required) {
        return navigate(
          PATH_NAMES.applicationDocuments({
            applicationId: rowData.application_uid,
          }),
        );
      }

      navigate(
        PATH_NAMES.application({ applicationId: rowData.application_uid }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const getRowHeight = () => {
    return 78;
  };

  const getAgGridLocale = () => {
    return locale === 'de'
      ? { ...AG_GRID_LOCALE_DE, dateFormatOoo: 'jjjj.mm.tt' }
      : AG_GRID_LOCALE_EN;
  };

  if (error || isContextError) {
    return (
      <ErrorCard
        title={t`Sorry an error has occurred`}
        message={t`Please reload the page to try again.`}
      />
    );
  }

  const handleFilterChanged = () => {
    if (gridRef.current) {
      registerNativeAgGridFilters(gridRef.current.api, {
        saveFilter,
        removeFilter,
        getFilterValues,
      });

      if (status !== 'loading' && status !== 'empty') {
        const displayedRowCount = gridRef.current.api.getDisplayedRowCount();

        if (displayedRowCount === 0) {
          setStatus('no results');
        } else {
          setStatus('success');
        }
        setNumberOfRows(displayedRowCount);
      }
    }
  };

  return (
    <div
      className={clsx('ag-theme-alpine', styles.tableWrapper)}
      data-testid="app-table"
    >
      <TableStatus />
      <AgGridReact
        ref={gridRef}
        onFilterChanged={handleFilterChanged}
        onGridReady={() => setIsGridReady(true)}
        rowData={applications}
        defaultColDef={{
          wrapText: true,
          autoHeight: true,
          minWidth: 130,
          resizable: false,
        }}
        suppressMovableColumns={true}
        skipHeaderOnAutoSize={true}
        headerHeight={75}
        rowHeight={72}
        getRowHeight={getRowHeight}
        suppressColumnVirtualisation={process.env.NODE_ENV === 'test'}
        enableCellTextSelection={true}
        ensureDomOrder={true}
        onFirstDataRendered={sizeColumnsToFit}
        onColumnVisible={sizeColumnsToFit}
        rowSelection="single"
        onRowClicked={onRowClicked}
        onGridSizeChanged={sizeColumnsToFit}
        components={{
          CreatedCell,
          ApplicationStatusCell,
          ApprovalAmountCell,
          OfferExpiryDateCell,
          TableSelectValuesFilter,
          CompanyCell,
          ProductTypeCell,
          TermLengthCell,
          agColumnHeader: TableColumnHeader,
          customNoRowsOverlay: NoRowsWithSearchOverlay,
        }}
        animateRows={true}
        columnDefs={ColumnDefinitions({
          hasPrimeLoans: splitTests?.hasAccessToPrimeLoans,
          showReferredByColumn: !!(
            introducer?.master_broker || introducerCompany?.shared_applications
          ),
        })}
        localeText={getAgGridLocale()}
      />
    </div>
  );
};
